import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import { findLocale } from 'services/translation';
import {
  getSavedTimezone,
  convertDateTimezone,
  timezoneCode,
} from 'ui_system/utilities/timezoneUtils';
import { localeDateFormats } from 'views/plugins/tours/locales/localMomentDateFormats';

interface RootUnit {
  noun_singular: string;
  noun_plural: string;
}

interface BookingInfoProps {
  date: string;
  time: string;
  duration: string;
  numberOfGuests: number;
  rootUnit: RootUnit;
  online?: boolean;
  tourTimezone?: string;
  isoTime?: boolean;
  locationLabel?: string;
}

export const BookingInfo = ({
  date,
  time,
  duration,
  numberOfGuests,
  rootUnit = {
    noun_singular: 'guest',
    noun_plural: 'guests',
  },
  online,
  tourTimezone,
  isoTime,
  locationLabel,
}: BookingInfoProps) => {
  const locale = findLocale() || 'en';
  moment.locale(locale);

  const getConvertedDateTime = () => {
    if (!online) return null;

    const timezone = getSavedTimezone();
    return {
      dateTime: convertDateTimezone({
        originalTimezone: tourTimezone,
        timezone,
        date: date.trim(),
        dateFormat: 'MMM DD, YYYY',
        time: time.replace(/\s+/g, ''),
        timeFormat: 'h:mma',
      }),
      timezone,
    };
  };

  const formatDate = () => {
    const dateObject = moment(date);

    if (!dateObject.isValid()) {
      console.error('Invalid date');
      return date;
    }

    const converted = getConvertedDateTime();
    if (converted) {
      return converted.dateTime.format('MMM D, YYYY');
    }

    const dateFormat = localeDateFormats(locale);
    return dateObject.format(dateFormat);
  };

  const formatTime = () => {
    const converted = getConvertedDateTime();
    if (converted) {
      const timeFormat = isoTime ? 'HH:mm' : 'h:mm a';
      return `${converted.dateTime.format(timeFormat)} (${timezoneCode(converted.timezone)})`;
    }

    if (isoTime) {
      return moment(time.trim(), ['h:mm A']).format('HH:mm');
    }

    return time;
  };

  return (
    <div className="CheckoutSummary-ContentBox Request-Information">
      <table>
        <tbody>
          {locationLabel && (
            <tr>
              <td className="label">
                <FormattedMessage
                  defaultMessage="Location"
                  description="Location"
                />
              </td>
              <td className="value">{locationLabel}</td>
            </tr>
          )}
          <tr>
            <td className="label">
              <FormattedMessage defaultMessage="Date" description="Date" />
            </td>
            <td className="value">{formatDate()}</td>
          </tr>
          <tr>
            <td className="label">
              <FormattedMessage defaultMessage="Time" description="Time" />
            </td>
            <td className="value">{formatTime()}</td>
          </tr>
          {duration && (
            <tr>
              <td className="label">
                <FormattedMessage
                  defaultMessage="Duration"
                  description="Duration"
                />
              </td>
              <td className="value">{duration}</td>
            </tr>
          )}
          <tr>
            <td className="label" data-testid="number-of-guests-label">
              <FormattedMessage
                defaultMessage="Number of {rootUnit}"
                description="Number of guests"
                values={{ rootUnit: rootUnit?.noun_plural }}
              />
            </td>
            <td className="value" data-testid="number-of-guests-value">
              {numberOfGuests}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};
